import { Box, Button, Grid, Typography } from "@mui/material";
import React from "react";
import { SideBarWidth } from "../../utils/SideBarWidth";
import { Colors } from "../../Theme/Theme";
import ShieldIcon from "@mui/icons-material/Shield";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SpeedIcon from "@mui/icons-material/Speed";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const PUSHBUTTON_INSURANCE_URL = "https://www.pushbutton.in/auth/role";

const InsurancePolicy = () => {
  const cardData = [
    { icon: "cashless", title: "Anywhere Cashless Claims", description: "Avail cashless claims across India with 14000+ network hospitals." },
    { icon: "support", title: "24x7 Customer Service", description: "Call us for claim intimation, telehealth services and queries." },
    { icon: "inhouse", title: "In-house Claim Settlement", description: "Claims settled by our qualified in-house team, no TPA involved." },
    { icon: "speed", title: "Fast Claim Settlement", description: "90% cashless claims settled within 2hrs, 92% reimbursement within 7 days." },
    { icon: "hospital", title: "Quality Network Hospitals", description: "Best health insurance plans under valuable network hospitals." },
    { icon: "award", title: "Industry Awards", description: "Awarded for innovative products, best claim settlement and service." },
  ];

  const whyChooseUsData = [
    { icon: "hospital", value: "1000+", description: "Network Hospitals" },
    { icon: "verified", value: "89%", description: "Claims in 2 Hours" },
    { icon: "office", value: "300+", description: "Branch Offices" },
    { icon: "shield", value: "1000+", description: "Claims Settled" },
  ];

  const getCardIcon = (type) => {
    const sx = { fontSize: 24, color: Colors.insuranceGreen };
    const map = {
      cashless: <HealthAndSafetyIcon sx={sx} />, support: <SupportAgentIcon sx={sx} />,
      inhouse: <MedicalServicesIcon sx={sx} />, speed: <SpeedIcon sx={sx} />,
      hospital: <LocalHospitalIcon sx={sx} />, award: <EmojiEventsIcon sx={sx} />,
      verified: <VerifiedIcon sx={sx} />, office: <AccountBalanceIcon sx={sx} />,
      shield: <ShieldIcon sx={sx} />,
    };
    return map[type] || <ShieldIcon sx={sx} />;
  };

  return (
    <Box sx={{
      flexGrow: 1, width: { xs: "100%", md: `calc(100% - ${SideBarWidth}px)` },
      ml: { md: `${SideBarWidth}px` }, minHeight: "100vh", backgroundColor: Colors.bgColor,
    }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <HealthAndSafetyIcon sx={{ fontSize: 22, color: Colors.insuranceGreen }} />
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, fontSize: { xs: "20px", md: "24px" }, letterSpacing: "-0.02em" }}>
              Insurance Policy
            </Typography>
          </Box>
          <Typography sx={{ color: Colors.secondaryColor, fontSize: "14px" }}>
            Get comprehensive health and life insurance coverage.
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{
            backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.greenBorder}`,
            p: { xs: 3, md: 4 },
          }}>
            <Typography sx={{ fontWeight: 700, color: Colors.primaryTextColor, mb: 1.5, fontSize: { xs: "22px", md: "28px" }, lineHeight: 1.2 }}>
              Insurance is the Key to Your{" "}
              <Box component="span" sx={{ color: Colors.insuranceGreen }}>Financial Security</Box>
            </Typography>
            <Typography sx={{ color: Colors.secondaryColor, mb: 3, maxWidth: 560, fontSize: "14px" }}>
              Continue to our insurance partner portal to explore plans, register, and manage policies.
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
              {[
                { icon: <ShieldIcon sx={{ fontSize: 18 }} />, label: "Health Cover" },
                { icon: <FamilyRestroomIcon sx={{ fontSize: 18 }} />, label: "Family Plan" },
                { icon: <LocalHospitalIcon sx={{ fontSize: 18 }} />, label: "Cashless" },
                { icon: <SpeedIcon sx={{ fontSize: 18 }} />, label: "Fast Claims" },
              ].map((item, i) => (
                <Box key={i} sx={{
                  display: "flex", alignItems: "center", gap: 0.8, px: 1.5, py: 0.8,
                  borderRadius: "8px", backgroundColor: Colors.greenSoft, border: `1px solid ${Colors.greenBorder}`,
                }}>
                  <Box sx={{ color: Colors.insuranceGreen, display: "flex" }}>{item.icon}</Box>
                  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: Colors.primaryTextColor }}>{item.label}</Typography>
                </Box>
              ))}
            </Box>

            <Button
              variant="contained"
              size="large"
              endIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
              onClick={() => window.open(PUSHBUTTON_INSURANCE_URL, "_blank", "noopener,noreferrer")}
              sx={{
                backgroundColor: Colors.insuranceGreen, color: "#FFFFFF", fontWeight: 600,
                py: 1.25, px: 3, borderRadius: "8px", fontSize: "15px",
                boxShadow: "0 2px 6px rgba(5,150,105,0.2)",
                "&:hover": { backgroundColor: Colors.insuranceGreenDark },
              }}
            >
              Open insurance portal
            </Button>
          </Box>
        </Box>

        <Box sx={{
          display: "flex", flexWrap: "wrap", gap: 2, mb: 3,
          backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.greenBorder}`,
          p: { xs: 2, md: 0 }, overflow: "hidden",
        }}>
          {whyChooseUsData.map((item, i) => (
            <Box key={i} sx={{
              flex: { xs: "1 1 45%", md: 1 }, textAlign: "center", py: { xs: 2, md: 3 }, px: 2,
              borderRight: { md: i < whyChooseUsData.length - 1 ? `1px solid ${Colors.greenBorder}` : "none" },
            }}>
              <Box sx={{ display: "inline-flex", width: 40, height: 40, borderRadius: "10px", backgroundColor: Colors.greenSoft, alignItems: "center", justifyContent: "center", mb: 1 }}>
                {getCardIcon(item.icon)}
              </Box>
              <Typography sx={{ fontWeight: 800, color: Colors.insuranceGreen, fontSize: { xs: "22px", md: "28px" }, lineHeight: 1 }}>
                {item.value}
              </Typography>
              <Typography sx={{ color: Colors.secondaryColor, fontSize: "12px", mt: 0.3 }}>{item.description}</Typography>
            </Box>
          ))}
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {cardData.map((item, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Box sx={{
                backgroundColor: Colors.white, borderRadius: "12px", border: `1px solid ${Colors.dividerColor}`,
                p: 2.5, height: "100%",
                transition: "all 0.15s ease",
                "&:hover": { borderColor: Colors.greenBorder },
              }}>
                <Box sx={{
                  width: 40, height: 40, borderRadius: "10px", mb: 1.5,
                  backgroundColor: Colors.greenSoft,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {getCardIcon(item.icon)}
                </Box>
                <Typography sx={{ fontWeight: 600, color: Colors.primaryTextColor, mb: 0.5, fontSize: "14px" }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: Colors.secondaryColor, lineHeight: 1.5, fontSize: "12px" }}>
                  {item.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default InsurancePolicy;
